import React, { useMemo, useState, useCallback } from 'react';
import { Avatar, Badge, Button, Menu } from '@mantine/core';
import {
    IconChevronDown,
    IconLogout,
    IconArrowBackUp,
} from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { useToggle } from '@mantine/hooks';

import { useAuth } from '../../context/AuthProvider';
import getColorByUuid from '../../helpers/userColorMap';

function NavbarUserSelect() {
    const [menuOpen, toggle] = useToggle([false, true]);
    const { authUser, isImpersonating, switchBack } = useAuth();

    const [loading, setLoading] = useState(false);

    const { logout: authLogout } = useAuth();
    const navigate = useNavigate();

    const initials = useMemo(() => {
        if (!authUser) {
            return '';
        }

        return `${authUser.firstName[0].toUpperCase()}${authUser.lastName[0].toUpperCase()}`;
    }, [authUser]);

    const logout = useCallback(async () => {
        toggle();
        try {
            setLoading(true);
            await authLogout();
        } finally {
            setLoading(false);
        }
    }, [authLogout, navigate]);

    return (
        <>
            {isImpersonating && (
                <Badge
                    color="orange"
                    variant="filled"
                    size="sm"
                    className="mr-1"
                >
                    Impersonating
                </Badge>
            )}
            <Menu
                width={240}
                closeOnItemClick={false}
                opened={menuOpen}
                onClose={toggle}
            >
                <Menu.Target>
                    <Button
                        className={`text-white text-md font-light px-1 max-w-[280px] flex flex-1 ${
                            isImpersonating
                                ? 'bg-orange-500/30 ring-2 ring-orange-400'
                                : 'bg-white/10'
                        }`}
                        rightSection={
                            <IconChevronDown
                                color="white"
                                size={20}
                                className="mx-2"
                            />
                        }
                        loading={loading}
                        onClick={() => toggle()}
                    >
                        <div className="p-0.5 bg-white mr-3 rounded-full">
                            <Avatar
                                radius="xl"
                                color={getColorByUuid(authUser?.id ?? '')}
                            >
                                {initials}
                            </Avatar>
                        </div>
                        <div>
                            <p className="text-left text-md font-semibold">
                                {authUser?.firstName} {authUser?.lastName}
                            </p>
                            <p className="text-xs text-left font-semibold">
                                {authUser?.designation}
                            </p>
                        </div>
                    </Button>
                </Menu.Target>
                <Menu.Dropdown>
                    {isImpersonating && (
                        <Menu.Item
                            onClick={() => switchBack()}
                            leftSection={<IconArrowBackUp />}
                            className="text-md font-medium hover:bg-orange-500/10 hover:text-orange-600"
                        >
                            Switch back to your account
                        </Menu.Item>
                    )}
                    <Menu.Item
                        onClick={() => logout()}
                        leftSection={<IconLogout />}
                        className="text-md font-medium hover:bg-primary/10 hover:text-primary"
                    >
                        Logout
                    </Menu.Item>
                </Menu.Dropdown>
            </Menu>
        </>
    );
}

export default NavbarUserSelect;
